/**
 * ActiveIntegrationBadges — QSR trust strip.
 *
 * Each badge shows the integration's brand LOGO, its name, then a pulsing green
 * "Active" dot when connected (otherwise "Available"). Logos load from Google's
 * favicon service with a tinted-initial fallback so a badge never renders blank.
 */
import React, { useState } from 'react';

const QSR_STACK = [
  { key: 'lightspeed', label: 'Lightspeed', tint: '#E4002B', domain: 'lightspeedhq.com', feeds: 'POS sales, items & order accuracy' },
  { key: 'square', label: 'Square', tint: '#3E4348', domain: 'squareup.com', feeds: 'Payments, tenders & average check' },
  { key: 'toast', label: 'Toast', tint: '#FF5100', domain: 'toasttab.com', feeds: 'Kitchen/KDS timing & menu mix' },
  { key: 'tanda', label: 'Tanda', tint: '#00B2A9', domain: 'tanda.co', feeds: 'Rosters, time & attendance, hours & SPLH' },
  { key: 'doshii', label: 'Doshii', tint: '#5B2A86', domain: 'doshii.com', feeds: 'Order aggregation across channels' },
  { key: 'ubereats', label: 'Uber Eats', tint: '#06C167', domain: 'ubereats.com', feeds: 'Delivery orders, fees & ratings' },
  { key: 'doordash', label: 'DoorDash', tint: '#FF3008', domain: 'doordash.com', feeds: 'Delivery orders, fees & ratings' },
];

const DEFAULT_CONNECTED = ['lightspeed', 'square', 'toast', 'tanda', 'doshii', 'ubereats', 'doordash'];

function BrandLogo({ provider, active }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <span
        className="inline-grid place-items-center h-5 w-5 rounded-md text-[10px] font-bold text-white shrink-0"
        style={{ backgroundColor: provider.tint }}
        aria-hidden
      >
        {provider.label[0]}
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${provider.domain}&sz=64`}
      alt={`${provider.label} logo`}
      width={20}
      height={20}
      loading="lazy"
      onError={() => setErr(true)}
      className={`h-5 w-5 rounded-md object-contain shrink-0 ${active ? '' : 'grayscale opacity-60'}`}
    />
  );
}

export default function ActiveIntegrationBadges({ connected = DEFAULT_CONNECTED, className = '' }) {
  const connectedSet = new Set((connected || []).map((c) => String(c).toLowerCase()));

  return (
    <div className={`w-full ${className}`} data-testid="integration-trust-badges">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Connected systems
        </span>
        <span className="h-px flex-1 bg-border/60" />
      </div>
      <div className="flex flex-wrap gap-2">
        {QSR_STACK.map((p) => {
          const active = connectedSet.has(p.key);
          return (
            <div
              key={p.key}
              data-testid={`integration-badge-${p.key}`}
              title={`${p.label}${active ? ' · Active' : ' · Available'} — ${p.feeds}`}
              className={`group flex items-center gap-2 rounded-full border pl-2 pr-3 py-1.5 text-sm font-medium transition-all ${
                active
                  ? 'bg-white border-border shadow-sm hover:shadow-md'
                  : 'bg-muted/40 border-transparent text-muted-foreground'
              }`}
            >
              <BrandLogo provider={p} active={active} />
              <span className={active ? 'text-foreground' : ''}>{p.label}</span>
              {active ? (
                <span className="flex items-center gap-1 pl-1" data-testid={`integration-active-${p.key}`}>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-[11px] font-semibold text-emerald-600">Active</span>
                </span>
              ) : (
                <span className="text-[11px] font-medium text-muted-foreground/70 pl-1">Available</span>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="integration-feeds-note">
        All systems live — POS sales &amp; accuracy, payments &amp; average check, KDS timing, rosters &amp; labour, order aggregation and delivery orders/fees/ratings flowing into Corvana.
      </p>
    </div>
  );
}
